import { useState, useEffect, useContext, useRef } from "react";
import { fetchCustomerById } from "../../api/fetch";
import { AuthContext } from "../../context/auth-context";
import LoadingSpinner from "../UI/LoadingSpinner";
import { extendBooking } from "../../api/add";
import { useNavigate } from "react-router-dom";

export default function ExtendBooking({ booking, setExtend, refresh }) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [warning, setWarning] = useState(false);
  const [customer, setCustomer] = useState();
  const [type, setType] = useState("now");

  // Declare Refs
  const bookingFeeRef = useRef();
  const taxesRef = useRef();
  const totalFeeRef = useRef();

  const fetchCustomer = () => {
    setLoading(true);
    fetchCustomerById(user, booking.customer.id)
      .then((data) => {
        setCustomer(data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  // Calculate and set total fee
  const calculateTotalFee = () => {
    const bookingFee = bookingFeeRef.current.value;
    const taxes = taxesRef.current.value;
    const totalFee = Number(bookingFee) + Number(taxes);
    totalFeeRef.current.value = "$" + totalFee.toString();
  };

  const extendBookingHandler = async (event) => {
    event.preventDefault();
    const end_date = event.target.end_date.value;
    const end_time = event.target.end_time.value;
    const miles_included = event.target.miles_included.value;
    const mileage_rate = event.target.mileage_rate.value;
    const drop_off_location = event.target.drop_off_location.value;
    const booking_fee = event.target.booking_fee.value;
    const taxes = event.target.taxes.value;
    const charge = event.target.charge.value;
    const payment_method_id = event.target.payment_method_id?.value;
    const days_until_due = event.target.days_until_due?.value;

    if (charge === "payment" && !payment_method_id) {
      setWarning("Please select a payment method.");
      return;
    }
    if (charge === "invoice" && !days_until_due) {
      setWarning("Please enter a number of days until due.");
      return;
    }

    const payload = {
      bookingId: booking.id,
      customerId: customer.id,
      end_date,
      end_time,
      miles_included,
      mileage_rate,
      drop_off_location,
      booking_fee,
      taxes,
      charge,
      payment_method_id,
      days_until_due,
    };

    setLoading(true);
    try {
      await extendBooking(user, payload);
      setExtend(false);
      refresh();
      setLoading(false);
    } catch (err) {
      console.log(err);
      setWarning(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomer();
  }, []);

  if (loading)
    return (
      <div className="flex items-center justify-center h-full md:col-span-3">
        <LoadingSpinner />
      </div>
    );

  return (
    <form
      onSubmit={extendBookingHandler}
      className="grid items-end gap-4 md:grid-cols-4 md:col-span-3"
    >
      <p className="text-lg font-semibold md:col-span-4">Extend Booking:</p>
      <div className="grid">
        <label className="text-sm">END DATE:</label>
        <input
          className="form-input"
          name="end_date"
          type="date"
          required
          placeholder="HH:MM"
        />
      </div>
      <div className="grid">
        <label className="text-sm">END TIME:</label>
        <input
          className="form-input"
          name="end_time"
          type="time"
          required
          placeholder="HH:MM"
        />
      </div>
      <div className="grid">
        <label className="text-sm">MILES INCLUDED:</label>
        <input
          className="form-input"
          name="miles_included"
          defaultValue={booking.miles_included}
          required
        />
      </div>
      <div className="grid">
        <label className="text-sm">EXTRA MILEAGE:</label>
        <input
          className="form-input"
          name="mileage_rate"
          defaultValue={booking.mileage_rate}
        />
      </div>
      <div className="grid">
        <label className="text-sm">DROP OFF LOCATION:</label>
        <input
          className="form-input"
          name="drop_off_location"
          defaultValue={booking.drop_off_location}
          type="text"
          required
        />
      </div>
      <div className="grid">
        <label className="text-sm">BOOKING FEE:</label>
        <input
          ref={bookingFeeRef}
          onChange={calculateTotalFee}
          className="form-input"
          name="booking_fee"
        />
      </div>

      <div className="grid">
        <label className="text-sm">TAXES:</label>
        <input
          ref={taxesRef}
          onChange={calculateTotalFee}
          className="form-input"
          name="taxes"
        />
      </div>
      <div className="grid">
        <label className="text-sm">TOTAL:</label>
        <input
          ref={totalFeeRef}
          className="form-input-disabled"
          name="total_fee"
          disabled
        />
      </div>
      <div className="grid">
        <label className="text-sm">CHARGE CUSTOMER:</label>
        <select
          onChange={(e) => setType(e.target.value)}
          className="form-input"
          name="charge"
          defaultValue={"now"}
        >
          <option value={"now"}>Now</option>
          <option value={"invoice"}>Send Invoice</option>
          <option value={"none"}>Do Not Charge</option>
        </select>
      </div>
      {type === "now" && (
        <div className="grid">
          <label className="text-sm">PAYMENT METHOD:</label>
          {customer?.default_payment_method ? (
            <select className="form-input" name="payment_method_id" required>
              <option value={customer.default_payment_method.id}>
                {customer.default_payment_method.brand}{" "}
                {customer.default_payment_method.last4}
              </option>
              {customer.payment_methods &&
                customer.payment_methods.map((pm, idx) => (
                  <option key={idx} value={pm.id}>
                    {pm.brand} {pm.last4}
                  </option>
                ))}
            </select>
          ) : (
            <input
              className="form-input-disabled"
              name="payment_method_id"
              placeholder="No Payment Methods Attached"
              disabled
            />
          )}
        </div>
      )}
      {type === "invoice" && (
        <div className="grid">
          <label className="text-sm">DAYS UNTIL DUE:</label>
          <input
            type="number"
            name="days_until_due"
            className="form-input"
            required
          />
        </div>
      )}
      {type === "none" && (
        <div className="grid">
          <label className="text-sm">BOOKING STATUS:</label>
          <select className="form-input" name="status">
            <option value="active">Active</option>
          </select>
        </div>
      )}
      <button type="submit" className="btn-primary">
        Confirm
      </button>
      <div onClick={() => setExtend(false)} className="btn-warning">
        Cancel
      </div>
      {warning && <p className="text-red-600">{warning}</p>}
    </form>
  );
}
